import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSnackbar } from "../utils/GlobalSnackbar/GlobalSnackbarContext";
import WEB_TOUR_STEPS from "./registry";
import { setTourActive } from "./tourRuntime";
import { hasTourAction, runTourAction } from "./tourActions";
import {
  closeIfOpensWithin,
  closeTourModal,
  isTourModalOpen,
  openTourModalTags,
} from "./tourModals";
import {
  findAnchor,
  isConcreteRoute,
  isOnTourRoute,
  keepsNew,
  knownItems,
  matchRoute,
  newItems,
  popupItems,
  routeForStep,
  stepModals,
  unknownIds,
  waitFor,
} from "./tourEngine";
import {
  LOG_TOUR_EVENTS,
  MARK_TOUR_COMPLETED,
  MARK_TOURS_POPPED,
  MARK_TOURS_SEEN,
  MY_TOURS,
} from "./tourGql";
import TourOverlay from "./TourOverlay";
import TourHub from "./TourHub";
import { lockPage, sealLayer } from "./tourLockout";
import "./tours.css";

/**
 * Feature tours / What's New / Help — the web engine.
 * Plan: bdr_mobile_app/plans/impl_feature_tours_whats_new_2026_09_28.md (§6),
 * authoring contract: TOUR_AUTHORING_GUIDE.md, server: registry §36.
 *
 * LOOK, DON'T TOUCH. While a tour runs:
 *   - everything outside the tour layer is `inert` (no pointer, no focus, no
 *     keys) — a pointer shield alone would still let a focused field take
 *     typing, which on a form is an auto-save (tourLockout.js);
 *   - events that START inside the layer (Enter on Next, a click on the
 *     shield) are sealed in it, so no document / window shortcut or
 *     click-outside handler on the page reacts to them (sealLayer);
 *   - the engine moves on only via its own Next / Back / Exit;
 *   - requests carry `x-bdr-tour: 1` so the server serves emails / threads
 *     without marking them read (tourRuntime.js → App.js authLink).
 * There is deliberately NO mutation guard (plan §6.4, owner G8).
 *
 * FAIL OPEN: any error in the card or the step flow ends the tour and lifts
 * the lockout. Tour state is memory-only; a reload always starts clean.
 */

const PLATFORM = "web";
const ANCHOR_TIMEOUT_MS = 4000;
// A tagged modal the tour asked to close must be gone within this, or the tour
// ends (fail open) rather than spotlighting something behind it.
const MODAL_CLOSE_TIMEOUT_MS = 2000;
// After the tour ends, a modal its last step asked for that registers late
// (Exit pressed while the action's render was still landing) is closed if it
// appears within this window.
const LATE_MODAL_WINDOW_MS = 1000;
const FLUSH_AT = 10;
const UNKNOWN_LOGGED_KEY = "bdr_tour_unknown_logged";
const APP_BUILD = process.env.REACT_APP_BUILD_ID || null;
// The popup is an office-app announcement: never over the public site or a
// customer's quote / invoice page, even for a logged-in user.
const OFFICE_PREFIX = "/bdr-office";

const INERT_CONTEXT = {
  enabled: false,
  items: [],
  newCount: 0,
  hasTours: false,
  running: false,
  openHub: () => {},
  startTour: () => {},
  dismiss: () => {},
};

const TourContext = createContext(INERT_CONTEXT);

/** Safe to call anywhere; outside a TourProvider it returns an inert value. */
export const useTours = () => useContext(TourContext);

function readUnknownLogged() {
  try {
    return new Set(JSON.parse(localStorage.getItem(UNKNOWN_LOGGED_KEY) || "[]"));
  } catch (_) {
    return new Set();
  }
}

function writeUnknownLogged(set) {
  try {
    localStorage.setItem(UNKNOWN_LOGGED_KEY, JSON.stringify([...set]));
  } catch (_) {
    /* private window / blocked storage: we may log the same id again; harmless */
  }
}

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return false;
  }
};

export function TourProvider({ active, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSnack } = useSnackbar();

  const [fetchTours] = useLazyQuery(MY_TOURS, { fetchPolicy: "network-only" });
  const [markSeenMut] = useMutation(MARK_TOURS_SEEN);
  const [markCompletedMut] = useMutation(MARK_TOUR_COMPLETED);
  const [markPoppedMut] = useMutation(MARK_TOURS_POPPED);
  const [logEventsMut] = useMutation(LOG_TOUR_EVENTS);

  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState([]); // KNOWN items only
  const [hub, setHub] = useState(null); // null | "popup" | "new" | "help"
  // { tourId, stepIndex, phase: "loading" | "showing", anchorEl }
  const [run, setRun] = useState(null);

  const runRef = useRef(null); // { tourId, stepIndex }
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const eventsRef = useRef([]);
  const tokenRef = useRef(0);
  const lateClosersRef = useRef([]); // cancel fns from closeIfOpensWithin
  const poppedThisLoginRef = useRef(false);

  // The layer every tour element portals into — outside #root, so it is the
  // one part of the page the lockout leaves interactive. Created once and
  // attached on MOUNT (not when a tour starts): the card measures and focuses
  // itself in a layout effect that runs before any parent effect, so a layer
  // attached later would give the first tour a 0×0 card and no focus.
  const layerRef = useRef(null);
  if (!layerRef.current && typeof document !== "undefined") {
    layerRef.current = document.getElementById("bdr-tour-layer") || document.createElement("div");
    layerRef.current.id = "bdr-tour-layer";
  }
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (layer && !layer.isConnected) document.body.appendChild(layer);
    return () => {
      if (layer && layer.isConnected) layer.remove();
    };
  }, []);

  // ── tracking ────────────────────────────────────────────────────────────
  const flushEvents = useCallback(() => {
    const batch = eventsRef.current.splice(0, 50);
    if (batch.length === 0) return;
    logEventsMut({ variables: { events: batch } }).catch(() => {
      /* tracking is best-effort; never surface it */
    });
  }, [logEventsMut]);

  const logEvent = useCallback(
    (tourId, event, extra = {}) => {
      eventsRef.current.push({
        tourId,
        event,
        stepIndex: Number.isInteger(extra.stepIndex) ? extra.stepIndex : null,
        reason: extra.reason || null,
        appBuild: APP_BUILD,
        ts: String(Date.now()),
      });
      if (eventsRef.current.length >= FLUSH_AT) flushEvents();
    },
    [flushEvents]
  );

  // ── loading ─────────────────────────────────────────────────────────────
  const endTourRef = useRef(null);

  const load = useCallback(async () => {
    let data;
    try {
      ({ data } = await fetchTours({ variables: { platform: PLATFORM } }));
    } catch (_) {
      return;
    }
    // An error (e.g. a server that predates myTours) resolves with no data:
    // stay quiet — no snack, no redirect.
    const res = data && data.myTours;
    if (!res) return;
    if (!res.enabled) {
      setEnabled(false);
      setItems([]);
      setHub(null);
      endTourRef.current?.("disabled");
      return;
    }
    setEnabled(true);

    // Server ids this build has no steps for: dropped, logged once per id.
    const unknown = unknownIds(res.items, WEB_TOUR_STEPS);
    if (unknown.length) {
      const logged = readUnknownLogged();
      const fresh = unknown.filter((id) => !logged.has(id));
      fresh.forEach((id) => {
        logEvent(id, "unknown_tour");
        logged.add(id);
      });
      if (fresh.length) {
        writeUnknownLogged(logged);
        flushEvents();
      }
    }

    setItems(knownItems(res.items, WEB_TOUR_STEPS));
  }, [fetchTours, flushEvents, logEvent]);

  // ── ending ──────────────────────────────────────────────────────────────
  const endTour = useCallback(
    (reason, { outOfDate = false } = {}) => {
      const cur = runRef.current;
      if (!cur) return;
      runRef.current = null;
      tokenRef.current += 1; // invalidates any in-flight step (and its waits)
      setRun(null);
      setTourActive(false);
      // Close every modal the tour opened (tourModals.js). After the run is
      // torn down and in its own try, so a throwing close can never leave the
      // page locked. A tour cannot START while a tagged modal is open, and the
      // page is inert while it runs, so every open tag here is the tour's.
      try {
        openTourModalTags().forEach(closeTourModal);
        const step = WEB_TOUR_STEPS[cur.tourId]?.steps?.[cur.stepIndex];
        stepModals(step)
          .filter((tag) => !isTourModalOpen(tag))
          .forEach((tag) => lateClosersRef.current.push(closeIfOpensWithin(tag, LATE_MODAL_WINDOW_MS)));
      } catch (err) {
        console.error("[tours] closing the tour's modals failed:", err);
      }
      try {
        if (reason === "completed") {
          logEvent(cur.tourId, "completed", { stepIndex: cur.stepIndex });
          // Completing is what clears an ASSIGNED tour (a start doesn't, see
          // keepsNew), so `isNew` goes false here for every tour.
          setItems((prev) =>
            prev.map((it) =>
              it.id === cur.tourId
                ? { ...it, isNew: false, completedAt: it.completedAt || String(Date.now()) }
                : it
            )
          );
          markCompletedMut({ variables: { tourId: cur.tourId } }).catch(() => {});
        } else {
          logEvent(cur.tourId, "exited", { stepIndex: cur.stepIndex, reason });
        }
        flushEvents();
        if (outOfDate) {
          handleSnack("This walkthrough is out of date and has been reported.", 4000);
        }
      } catch (err) {
        // Bookkeeping only — the tour is already ended above.
        console.error("[tours] end bookkeeping failed:", err);
      }
    },
    [flushEvents, handleSnack, logEvent, markCompletedMut]
  );
  endTourRef.current = endTour;

  // ── stepping ────────────────────────────────────────────────────────────
  const goToStepRef = useRef(null);
  const goToStep = useCallback(
    async (index, direction = 1) => {
      const cur = runRef.current;
      if (!cur) return;
      const def = WEB_TOUR_STEPS[cur.tourId];
      const item = itemsRef.current.find((it) => it.id === cur.tourId);
      if (!def) {
        endTour("error");
        return;
      }
      if (index < 0) index = 0;
      if (index >= def.steps.length) {
        endTour("completed");
        return;
      }
      const token = ++tokenRef.current;
      const stale = () => tokenRef.current !== token || !runRef.current;
      const step = def.steps[index];
      runRef.current = { ...cur, stepIndex: index };
      setRun({ tourId: cur.tourId, stepIndex: index, phase: "loading", anchorEl: null });

      const skipOrEnd = (event, reason) => {
        logEvent(cur.tourId, event, { stepIndex: index });
        if (step.optional) {
          const next = index + direction;
          goToStepRef.current(next < 0 ? index + 1 : next, next < 0 ? 1 : direction);
          return;
        }
        endTour(reason, { outOfDate: true });
      };

      try {
        // Close the tour's modals this step isn't shown inside, and wait until
        // they are really gone (unregistered = unmounted, tourModals.js), so
        // the anchor lookup below never lands behind a closing overlay.
        const keep = stepModals(step);
        const toClose = openTourModalTags().filter((tag) => !keep.includes(tag));
        if (toClose.length) {
          toClose.forEach(closeTourModal);
          const closed = await waitFor(
            () => toClose.every((tag) => !isTourModalOpen(tag)),
            MODAL_CLOSE_TIMEOUT_MS,
            stale
          );
          if (stale()) return;
          if (!closed) {
            endTour("modal_stuck", { outOfDate: true });
            return;
          }
        }

        const route = routeForStep(def, index, item && item.startMode);
        if (route && !matchRoute(route, window.location.pathname)) {
          if (!isConcreteRoute(route)) {
            // A record page the engine can't reach by itself.
            skipOrEnd("anchor_missing", "route_unreachable");
            return;
          }
          // Always one of this tour's own routes, so the navigation watcher
          // below never mistakes it for the user leaving.
          navigate(route);
        }

        if (step.action) {
          const ok = await waitFor(() => hasTourAction(step.action), ANCHOR_TIMEOUT_MS, stale);
          if (stale()) return;
          if (!ok) {
            skipOrEnd("action_missing", "action_missing");
            return;
          }
          runTourAction(step.action);
        }

        // Every modal this step is shown inside must be open — opened by this
        // step's action or kept from the step before. If not, the tour can't
        // show what it describes: skip an optional step, else end out of date.
        if (keep.length) {
          const opened = await waitFor(() => keep.every(isTourModalOpen), ANCHOR_TIMEOUT_MS, stale);
          if (stale()) return;
          if (!opened) {
            skipOrEnd("action_missing", "modal_missing");
            return;
          }
        }

        let anchorEl = null;
        if (step.anchor) {
          // Inside a modal, prefer the LAST match: modals portal after #root.
          anchorEl = await waitFor(
            () => findAnchor(step.anchor, undefined, { last: keep.length > 0 }),
            ANCHOR_TIMEOUT_MS,
            stale
          );
          if (stale()) return;
          if (!anchorEl) {
            skipOrEnd("anchor_missing", "anchor_missing");
            return;
          }
          try {
            anchorEl.scrollIntoView({
              block: "center",
              inline: "nearest",
              behavior: prefersReducedMotion() ? "auto" : "smooth",
            });
          } catch (_) {
            /* old browsers without options: harmless */
          }
        }
        if (stale()) return;
        setRun({ tourId: cur.tourId, stepIndex: index, phase: "showing", anchorEl });
      } catch (err) {
        // Fail open: never leave a user behind a shield.
        console.error("[tours] step failed, ending tour:", err);
        endTour("error");
      }
    },
    [endTour, logEvent, navigate]
  );
  goToStepRef.current = goToStep;

  const startTour = useCallback(
    (tourId) => {
      const def = WEB_TOUR_STEPS[tourId];
      const item = itemsRef.current.find((it) => it.id === tourId);
      if (!def || !item || !enabled) return;
      // A walkthrough closes the modals it opens; it must never close one the
      // user already had open (with their work in it). So it doesn't start over
      // an open tagged modal. Not on a restart: those are the old tour's,
      // closing as it ends.
      if (!runRef.current && openTourModalTags().length) {
        handleSnack("Close the open window first, then start the walkthrough.", 4000);
        return;
      }
      if (runRef.current) endTour("restart");
      // A late closer from the LAST tour (including the restart just above)
      // must not close a window this one opens.
      lateClosersRef.current.forEach((cancel) => cancel());
      lateClosersRef.current = [];
      setHub(null);
      const now = String(Date.now());
      // An assigned tour stays new until completed (keepsNew): only the seen
      // stamp changes for it here.
      setItems((prev) =>
        prev.map((it) =>
          it.id === tourId
            ? { ...it, isNew: keepsNew(it) ? it.isNew : false, seenAt: it.seenAt || now }
            : it
        )
      );
      markSeenMut({ variables: { tourIds: [tourId], via: "started" } }).catch(() => {});
      runRef.current = { tourId, stepIndex: 0 };
      setTourActive(true);
      logEvent(tourId, "started", { stepIndex: 0 });
      goToStep(0, 1);
    },
    [enabled, endTour, goToStep, handleSnack, logEvent, markSeenMut]
  );

  const dismiss = useCallback(
    (tourIds) => {
      // An assigned tour can't be dismissed (keepsNew) — the hub hides the
      // button; this is the belt and braces for "Dismiss all".
      const keep = new Set(itemsRef.current.filter(keepsNew).map((it) => it.id));
      const ids = (tourIds || []).filter((id) => id && !keep.has(id));
      if (!ids.length) return;
      const now = String(Date.now());
      setItems((prev) =>
        prev.map((it) =>
          ids.includes(it.id) ? { ...it, isNew: false, seenAt: it.seenAt || now } : it
        )
      );
      markSeenMut({ variables: { tourIds: ids, via: "dismissed" } }).catch(() => {});
    },
    [markSeenMut]
  );

  const openHub = useCallback(
    (mode = "help") => {
      setHub(mode);
      load();
    },
    [load]
  );

  // ── lifecycle ───────────────────────────────────────────────────────────
  // Load on login; reset everything on logout.
  useEffect(() => {
    if (active) {
      load();
      return undefined;
    }
    endTourRef.current?.("logout");
    setEnabled(false);
    setItems([]);
    setHub(null);
    poppedThisLoginRef.current = false;
    return undefined;
  }, [active, load]);

  // One-time What's New popup (D1): the first time this login is on an office
  // page with known, new, never-popped tours. Server poppedAt keeps each tour
  // to one popup ever; this ref keeps it to one popup per login.
  useEffect(() => {
    // `hub` open (e.g. Help reloading items) → not now: markToursPopped must
    // only ever stamp ids that were actually shown in the popup.
    if (!enabled || hub || poppedThisLoginRef.current || runRef.current) return;
    if (!location.pathname.startsWith(OFFICE_PREFIX)) return;
    const pop = popupItems(items);
    if (!pop.length) return;
    poppedThisLoginRef.current = true;
    setHub((h) => h || "popup");
    const ids = pop.map((it) => it.id);
    const now = String(Date.now());
    setItems((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, poppedAt: now } : it)));
    markPoppedMut({ variables: { tourIds: ids } }).catch(() => {});
  }, [enabled, hub, items, location.pathname, markPoppedMut]);

  // Leaving the tour's pages ends it — including mid-load. The engine only
  // ever navigates to the tour's own routes, so it can't trip this itself.
  useEffect(() => {
    const cur = runRef.current;
    if (!cur) return;
    const def = WEB_TOUR_STEPS[cur.tourId];
    if (!isOnTourRoute(def, location.pathname)) endTour("nav");
  }, [location.pathname, endTour]);

  const running = !!run;
  useEffect(() => {
    if (!running) return undefined;
    const layer = layerRef.current;
    if (!layer.isConnected) document.body.appendChild(layer);
    const unlock = lockPage(layer);
    const unseal = sealLayer(layer);
    // Capture phase, before any page handler: Esc exits; keys aimed OUTSIDE
    // the layer are swallowed, and never type into a field.
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        event.preventDefault();
        endTourRef.current?.("esc");
        return;
      }
      if (!layer.contains(event.target)) {
        event.stopImmediatePropagation();
        const t = event.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) {
          event.preventDefault();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      unseal();
      unlock();
    };
  }, [running]);

  // Unmount: never leave the flag behind, or a late-modal closer running.
  useEffect(
    () => () => {
      setTourActive(false);
      lateClosersRef.current.forEach((cancel) => cancel());
      lateClosersRef.current = [];
    },
    []
  );

  const value = useMemo(() => {
    const visible = enabled ? items : [];
    return {
      enabled,
      items: visible,
      newCount: newItems(visible).length,
      hasTours: visible.length > 0,
      running,
      openHub,
      startTour,
      dismiss,
    };
  }, [enabled, items, running, openHub, startTour, dismiss]);

  const def = run ? WEB_TOUR_STEPS[run.tourId] : null;
  const item = run ? items.find((it) => it.id === run.tourId) : null;

  return (
    <TourContext.Provider value={value}>
      {children}
      {hub && enabled && (
        <TourHub
          mode={hub}
          items={items}
          registry={WEB_TOUR_STEPS}
          pathname={location.pathname}
          onStart={startTour}
          onDismiss={dismiss}
          onClose={() => setHub(null)}
        />
      )}
      {run && def && (
        <TourOverlay
          layer={layerRef.current}
          title={item ? item.title : ""}
          step={def.steps[run.stepIndex]}
          stepIndex={run.stepIndex}
          stepCount={def.steps.length}
          phase={run.phase}
          anchorEl={run.anchorEl}
          reducedMotion={prefersReducedMotion()}
          onNext={() => goToStep(run.stepIndex + 1, 1)}
          onBack={() => goToStep(run.stepIndex - 1, -1)}
          onExit={() => endTour("skip")}
          onCrash={() => endTour("error")}
        />
      )}
    </TourContext.Provider>
  );
}

export default TourProvider;
